// Using simpler approach without recharts due to TypeScript module resolution issues
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Card, 
  CardContent, 
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subMonths, startOfMonth, endOfMonth, isAfter, isValid } from 'date-fns';
import { 
  getExpenses, 
  getInvoices, 
  getProfitCalculation, 
  getExpenseSummaryByCategory,
  Expense,
  ExpenseCategory,
  ProfitResponse
} from '../../api/accounting';
import { useTranslation } from '../../utils/i18n-utils';

// Find the Invoice interface definition and update it
interface Invoice {
  id: number;
  invoiceNumber: string;
  amount: number;
  // Support both structures - the one defined in accounting.ts and the one returned from the backend
  payee?: string;
  purpose?: string;
  issueDate?: string;
  dueDate?: string;
  date?: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'pending';
  notes?: string;
  isRecurring?: boolean;
  recurringFrequency?: 'weekly' | 'monthly' | 'quarterly' | 'annually';
  nextRecurringDate?: string;
  userId?: number;
  createdAt?: string;
  updatedAt?: string;
  // Add customer property for the Sales-derived invoices
  customer?: {
    name: string;
    phone: string;
  };
}

const AccountingDashboard: React.FC = () => {
  const { t } = useTranslation();
  // Current date for calculations
  const currentDate = new Date();
  
  // Date range state - use current year dates instead of future dates
  const [startDate, setStartDate] = useState<Date>(startOfMonth(subMonths(currentDate, 3)));
  const [endDate, setEndDate] = useState<Date>(endOfMonth(currentDate));
  const [timeInterval, setTimeInterval] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('monthly');
  
  // Check for reset flag in localStorage
  useEffect(() => {
    const resetFlag = localStorage.getItem('accounting_date_reset');
    if (resetFlag === 'true') {
      console.log('Resetting accounting dashboard dates to defaults');
      
      // Reset to safe defaults
      const safeStartDate = startOfMonth(subMonths(new Date(), 3));
      const safeEndDate = endOfMonth(new Date());
      
      setStartDate(safeStartDate);
      setEndDate(safeEndDate);
      setTimeInterval('monthly');
      
      // Clear the flag
      localStorage.removeItem('accounting_date_reset');
      
      // Reload with safe dates
      setTimeout(() => loadData(), 100);
    }
  }, []);
  
  // Data state
  const [profitData, setProfitData] = useState<ProfitResponse | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [expensesByCategory, setExpensesByCategory] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [lastSalesUpdate, setLastSalesUpdate] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  
  // Load data function
  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Validate dates before sending to API
      if (!isValid(startDate) || !isValid(endDate)) {
        throw new Error('Invalid date range');
      }
      
      // Don't allow future end dates
      let safeEndDate = endDate;
      if (isAfter(safeEndDate, currentDate)) {
        safeEndDate = currentDate;
      }
      
      // Format dates for API calls
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(safeEndDate, 'yyyy-MM-dd');
      
      console.log(`Loading data from ${formattedStartDate} to ${formattedEndDate}`);
      
      // Fetch all data in parallel with individual error handling
      const results = await Promise.allSettled([
        getProfitCalculation(formattedStartDate, formattedEndDate, timeInterval),
        getExpenses(formattedStartDate, formattedEndDate),
        getInvoices(formattedStartDate, formattedEndDate),
        getExpenseSummaryByCategory(formattedStartDate, formattedEndDate)
      ]);
      
      // Process profit data
      if (results[0].status === 'fulfilled') {
        setProfitData(results[0].value);
      } else {
        console.error('Error loading profit data:', results[0].reason);
        setProfitData(null);
      }
      
      // Process expenses
      if (results[1].status === 'fulfilled') {
        setExpenses(results[1].value);
      } else { 
        console.error('Error loading expenses:', results[1].reason);
        setExpenses([]);
      }
      
      // Process invoices
      if (results[2].status === 'fulfilled') {
        setInvoices(results[2].value);
      } else {
        console.error('Error loading invoices:', results[2].reason);
        setInvoices([]);
      }
      
      // Process expense categories
      if (results[3].status === 'fulfilled') {
        // Format expense category data for pie chart - add defensive programming
        const categoryData = results[3].value.map(item => {
          // Ensure expenseCategory exists and has a name
          const categoryName = item?.expenseCategory?.name || 'Uncategorized';
          return {
            name: categoryName,
            value: Number(item.totalAmount || 0)
          };
        });
        setExpensesByCategory(categoryData);
      } else {
        console.error('Error loading expense categories:', results[3].reason);
        setExpensesByCategory([]);
      }
      
      // Check if all requests failed
      const allFailed = results.every(result => result.status === 'rejected');
      if (allFailed) {
        setError('Failed to load accounting data. Please try again or contact support.');
      }
      
    } catch (error: any) {
      console.error('Error loading accounting dashboard data:', error);
      setError(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  // Initial data load
  useEffect(() => {
    loadData();
  }, []);

  // Listen for custom sale created events
  useEffect(() => {
    // Define event handler
    const handleSaleCreated = () => {
      console.log('Sale created event received, refreshing accounting data');
      loadData();
    };

    // Add event listener
    window.addEventListener('moftah-sale-created', handleSaleCreated);

    // Clean up
    return () => {
      window.removeEventListener('moftah-sale-created', handleSaleCreated);
    };
  }, []);

  // Set up a timer to periodically check for new sales
  useEffect(() => {
    // Check localStorage for moftahInvoices
    const checkForSalesUpdates = () => {
      try {
        const savedInvoices = localStorage.getItem('moftahInvoices');
        if (savedInvoices) {
          const timestamp = JSON.parse(savedInvoices)?.length || 0;
          if (timestamp !== lastSalesUpdate) {
            console.log('Detected new sales, refreshing accounting data');
            setLastSalesUpdate(timestamp);
            loadData();
          }
        }
      } catch (error) {
        console.error('Error checking for sales updates:', error);
      }
    };

    // Check immediately
    checkForSalesUpdates();

    // Set up an interval to check every 30 seconds
    const intervalId = setInterval(checkForSalesUpdates, 30000);

    // Clean up the interval when component unmounts
    return () => clearInterval(intervalId);
  }, [lastSalesUpdate]);
  
  // Handle date range change
  const handleApplyDateRange = () => {
    // Validate dates before loading
    if (!isValid(startDate) || !isValid(endDate)) {
      setError('Please select valid dates');
      return;
    }
    
    // Don't allow end date before start date
    if (isAfter(startDate, endDate)) {
      setError('Start date cannot be after end date');
      return;
    }
    
    loadData();
  };

  // Handle error close
  const handleErrorClose = () => {
    setError(null);
  };
  
  // Calculate summary numbers (with fallbacks for missing data)
  const totalRevenue = profitData?.totals?.revenue || 0;
  const totalExpenses = profitData?.totals?.expenses || 0;
  const totalProfit = profitData?.totals?.netProfit || 0;
  const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
  
  // Get overdue invoices
  const overdueInvoices = invoices.filter(invoice => invoice.status === 'overdue');
  const totalOverdueAmount = overdueInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  
  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('accountingDashboard.title')}
      </Typography>
      
      {/* Date range selector */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker 
            label={t('common.startDate')}
            value={startDate}
            onChange={(newValue) => newValue && setStartDate(newValue)}
          />
          
          <DatePicker 
            label={t('common.endDate')}
            value={endDate}
            onChange={(newValue) => newValue && setEndDate(newValue)}
          />
        </LocalizationProvider>
        
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>{t('accountingDashboard.timeInterval')}</InputLabel>
          <Select
            value={timeInterval}
            label={t('accountingDashboard.timeInterval')}
            onChange={(e) => setTimeInterval(e.target.value as any)}
          >
            <MenuItem value="daily">{t('profitReport.intervals.daily')}</MenuItem>
            <MenuItem value="weekly">{t('profitReport.intervals.weekly')}</MenuItem>
            <MenuItem value="monthly">{t('profitReport.intervals.monthly')}</MenuItem>
            <MenuItem value="yearly">{t('profitReport.intervals.yearly')}</MenuItem>
          </Select>
        </FormControl>
        
        <Button 
          variant="contained" 
          onClick={handleApplyDateRange}
          disabled={loading}
        >
          {t('common.apply')}
        </Button>
      </Paper>

      {/* Error message */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleErrorClose}>
        <Alert onClose={handleErrorClose} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Financial summary cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                {t('accountingDashboard.totalRevenue')}
              </Typography>
              <Typography variant="h4">
                د.ل{totalRevenue.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                {t('accountingDashboard.totalExpenses')}
              </Typography>
              <Typography variant="h4">
                د.ل{totalExpenses.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                {t('accountingDashboard.netProfit')}
              </Typography>
              <Typography variant="h4" color={totalProfit >= 0 ? 'success.main' : 'error.main'}>
                د.ل{totalProfit.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                {t('accountingDashboard.profitMargin')}
              </Typography>
              <Typography variant="h4" color={profitMargin >= 0 ? 'success.main' : 'error.main'}>
                {profitMargin.toFixed(2)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Charts and data visualization */}
      <Grid container spacing={3}>
        {/* Profit Over Time Table */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 2, height: 380, overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              {t('accountingDashboard.profitOverTime')}
            </Typography>
            {profitData?.profitByPeriod ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('accountingDashboard.period')}</TableCell>
                      <TableCell align="right">{t('accountingDashboard.revenue')}</TableCell>
                      <TableCell align="right">{t('accountingDashboard.expenses')}</TableCell>
                      <TableCell align="right">{t('accountingDashboard.netProfit')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {profitData.profitByPeriod.map((period) => (
                      <TableRow key={period.timePeriod}>
                        <TableCell>{period.timePeriod}</TableCell>
                        <TableCell align="right">د.ل{period.revenue.toLocaleString()}</TableCell>
                        <TableCell align="right">د.ل{period.expenses.toLocaleString()}</TableCell>
                        <TableCell align="right" 
                          sx={{ color: period.netProfit >= 0 ? 'success.main' : 'error.main' }}
                        >
                          د.ل{period.netProfit.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="textSecondary">{loading ? t('accountingDashboard.loading') : t('accountingDashboard.noData')}</Typography>
            )}
          </Paper>
        </Grid>
        
        {/* Expense Breakdown Table */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2, height: 380, overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              {t('accountingDashboard.expenseBreakdown')}
            </Typography>
            {expensesByCategory.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('accountingDashboard.category')}</TableCell>
                      <TableCell align="right">{t('accountingDashboard.amount')}</TableCell>
                      <TableCell align="right">{t('accountingDashboard.percentage')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expensesByCategory.map((category) => {
                      const percentage = totalExpenses > 0 ? (category.value / totalExpenses) * 100 : 0;
                      return (
                        <TableRow key={category.name || 'unknown'}>
                          <TableCell>{category.name || t('accountingDashboard.uncategorized')}</TableCell>
                          <TableCell align="right">د.ل{(category.value || 0).toLocaleString()}</TableCell>
                          <TableCell align="right">{percentage.toFixed(1)}%</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="textSecondary">{loading ? t('accountingDashboard.loading') : t('accountingDashboard.noCategories')}</Typography>
            )}
          </Paper>
        </Grid>
        
        {/* Recent Invoices */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, height: 400, overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              {t('accountingDashboard.recentInvoices')}
            </Typography>
            
            {loading && (
              <Typography variant="body2" color="textSecondary">
                {t('accountingDashboard.loading')}
              </Typography>
            )}
            
            {!loading && invoices.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                {t('accountingDashboard.noInvoices')}
              </Typography>
            ) : (
              invoices.slice(0, 10).map((invoice) => (
                <Box 
                  key={invoice.id}
                  sx={{ 
                    mb: 2, 
                    p: 1, 
                    borderRadius: 1, 
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Grid container>
                    <Grid size={{ xs: 8 }}>
                      <Typography variant="subtitle2">
                        {invoice.invoiceNumber} - {invoice.customer?.name || t('accountingDashboard.unknownCustomer')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {invoice.notes || t('accountingDashboard.noDescription')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }} sx={{ textAlign: 'right' }}>
                      <Typography variant="subtitle2">
                        د.ل{invoice.amount ? invoice.amount.toLocaleString() : '0'}
                      </Typography>
                      <Typography 
                        variant="body2"
                        color={
                          invoice.status === 'paid' ? 'success.main' : 
                          invoice.status === 'overdue' ? 'error.main' : 
                          'warning.main'
                        }
                      >
                        {invoice.status ? invoice.status.toUpperCase() : t('accountingDashboard.unknown')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
        
        {/* Recent Expenses */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, height: 400, overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              {t('accountingDashboard.recentExpenses')}
            </Typography>
            
            {loading && (
              <Typography variant="body2" color="textSecondary">
                {t('accountingDashboard.loading')}
              </Typography>
            )}
            
            {!loading && expenses.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                {t('accountingDashboard.noExpenses')}
              </Typography>
            ) : (
              expenses.slice(0, 10).map((expense) => (
                <Box 
                  key={expense.id}
                  sx={{ 
                    mb: 2, 
                    p: 1, 
                    borderRadius: 1, 
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Grid container>
                    <Grid size={{ xs: 8 }}>
                      <Typography variant="subtitle2">
                        {expense.description || t('accountingDashboard.expense')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {expense.ExpenseCategory?.name || expense.expenseCategory?.name || t('accountingDashboard.uncategorized')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }} sx={{ textAlign: 'right' }}>
                      <Typography variant="subtitle2" color="error.main">
                        -د.ل{expense.amount.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {new Date(expense.date).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
        
        {/* Overdue Invoices Alert */}
        {overdueInvoices.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Paper 
              sx={{ 
                p: 2, 
                backgroundColor: 'error.light', 
                color: 'error.contrastText' 
              }}
            >
              <Typography variant="h6">
                {t('accountingDashboard.overdueInvoices', { count: overdueInvoices.length, plural: overdueInvoices.length > 1 ? 's' : '' })}
              </Typography>
              <Typography variant="body1">
                {t('accountingDashboard.totalOverdueAmount', { amount: `د.ل${totalOverdueAmount.toLocaleString()}` })}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default AccountingDashboard; 